import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { listTemplates } from '@/lib/api/tournament-templates';
import { ApiError } from '@/lib/api/core';
import { showErrorToast } from '@/lib/toast';
import type { TournamentTemplate } from '@swiss-magic/shared-types';
import type { TournamentFormController } from './useTournamentFormController';

// Story 078 — picker that prefills the create-tournament form from
// one of the director's templates. Hidden entirely (returns null)
// until the templates list resolves AND contains at least one
// entry — directors with no templates should see no extra UI.

interface Props {
  form: TournamentFormController;
}

export function StartFromTemplatePicker({ form }: Props) {
  const { t } = useTranslation('director');
  const [templates, setTemplates] = useState<TournamentTemplate[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listTemplates()
      .then((list) => {
        if (!cancelled) setTemplates(list);
      })
      .catch((err) => {
        if (cancelled) return;
        // Non-blocking: log + toast, then treat as empty so the
        // picker stays hidden and the form is still usable.
        console.error('Failed to load tournament templates', err);
        showErrorToast(
          err instanceof ApiError
            ? err.userMessage
            : t('tournament_form.start_from_template.load_failed_toast'),
        );
        setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (templates === null || templates.length === 0) return null;

  function handleSelect(id: string) {
    const tpl = (templates ?? []).find((tt) => tt.id === id);
    if (!tpl) return;
    form.applyTemplateValues(tpl.values);
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="start-from-template-picker">
        {t('tournament_form.start_from_template.label')}
      </Label>
      <Select onValueChange={handleSelect}>
        <SelectTrigger
          id="start-from-template-picker"
          data-testid="start-from-template-picker"
        >
          <SelectValue
            placeholder={t('tournament_form.start_from_template.placeholder')}
          />
        </SelectTrigger>
        <SelectContent>
          {templates.map((tpl) => (
            <SelectItem
              key={tpl.id}
              value={tpl.id}
              data-testid={`start-from-template-option-${tpl.name}`}
            >
              {tpl.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
